import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Star from './Star';

const PantryMap: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(mapRef, { once: true, amount: 0.2 });
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    if (isInView) {
      // After first animation completes + 3000ms delay, trigger loop
      const timeout = setTimeout(() => {
        setAnimationKey(1); // Change key to trigger loop
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isInView]);

  // Star positions from CSS with rotation
  const starPositions = [
    { left: 490.84, top: 1705.9, size: 174.32, rotate: 12.05 },
    { left: 666.84, top: 1321.9, size: 159.37, rotate: -8.6 },
    { left: 1898.84, top: 1529.9, size: 141.8, rotate: -8.52 },
    { left: 1544.99, top: 503.91, size: 146.76, rotate: -10.25 },
    { left: 670.87, top: 490.06, size: 127.64, rotate: -1.94 },
    { left: 455, top: 285.39, size: 159.19, rotate: -5.15 },
    { left: 904.45, top: 1477.68, size: 60, rotate: -12.67 },
    { left: 3452.73, top: 1546.29, size: 60, rotate: 9.54 },
    { left: 2545.84, top: 191.9, size: 141.27, rotate: -6.89 },
  ];

  return (
    <div className="w-full">
      <div className="tmf-pantry-map-container" ref={mapRef}>
        {/* Animated Map Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0"
        >
          {/* Purple volunteer access area (right side) */}
          <div
            className="absolute bg-[#BB95F8] rounded-[6px]"
            style={{ width: '2288px', height: '1092px', left: '1610.84px', top: '409.9px' }}
          />

          {/* Light blue user access area (main floor) */}
          <div
            className="absolute bg-[#C2E5FF] rounded-[6px]"
            style={{ width: '3408px', height: '1104px', left: '490.84px', top: '409.9px' }}
          />

          {/* Green refrigerated area (vertical left) */}
          <div
            className="absolute bg-[rgba(102,213,117,0.6)] rounded-[6px]"
            style={{ width: '128px', height: '848px', left: '762.84px', top: '665.9px' }}
          />

          {/* Green refrigerated area (horizontal top left) */}
          <div
            className="absolute bg-[rgba(102,213,117,0.6)] rounded-[6px]"
            style={{ width: '480px', height: '128px', left: '890.84px', top: '665.9px' }}
          />

          {/* Green refrigerated area (vertical middle) */}
          <div
            className="absolute bg-[rgba(102,213,117,0.6)] rounded-[6px]"
            style={{ width: '128px', height: '512px', left: '1242.84px', top: '793.9px' }}
          />

          {/* Green refrigerated area (horizontal middle) */}
          <div
            className="absolute bg-[rgba(102,213,117,0.6)] rounded-[6px]"
            style={{ width: '352px', height: '128px', left: '890.84px', top: '1177.9px' }}
          />

          {/* Green User Access Area indicator (top right) */}
          <div
            className="absolute bg-[rgba(102,213,117,0.6)] rounded-[6px]"
            style={{ width: '201.75px', height: '162.6px', left: '3295.84px', top: '185.9px' }}
          />

          {/* Orange Rack - Fresh produce Rack 4 */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '176px', height: '304px', left: '978.84px', top: '833.9px' }}
          />

          {/* Orange Rack - Fresh produce Rack 5 */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '176px', height: '304px', left: '1402.84px', top: '830.9px' }}
          />

          {/* Brown divider line (vertical) */}
          <div
            className="absolute bg-[#582C03] rounded-[3px]"
            style={{ width: '24px', height: '992px', left: '1586.84px', top: '409.9px' }}
          />

          {/* Orange Rack (left tall) */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '176px', height: '736px', left: '498.84px', top: '601.9px' }}
          />

          {/* Orange Rack 1 (next to brown divider) */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '176px', height: '992px', left: '1610.84px', top: '409.9px' }}
          />

          {/* Orange Rack 2 */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '480px', height: '128px', left: '1946.84px', top: '601.9px' }}
          />

          {/* Brown Packing Table 1 */}
          <div
            className="absolute bg-[#723C0A] rounded-[6px]"
            style={{ width: '352px', height: '245px', left: '2490.84px', top: '601.9px' }}
          />

          {/* Brown Packing Table 2 */}
          <div
            className="absolute bg-[#723C0A] rounded-[6px]"
            style={{ width: '352px', height: '245px', left: '2490.84px', top: '889.9px' }}
          />

          {/* Orange Rack 3 */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '480px', height: '123px', left: '1946.84px', top: '828.9px' }}
          />

          {/* Orange Rack 4 */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '480px', height: '123px', left: '1946.84px', top: '1054.9px' }}
          />

          {/* Orange Rack 8 */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '224px', height: '368px', left: '2970.84px', top: '601.9px' }}
          />

          {/* Tan Empty Boxes */}
          <div
            className="absolute bg-[#C69E79] rounded-[6px]"
            style={{ width: '224px', height: '225px', left: '2970.84px', top: '1000.9px' }}
          />

          {/* Orange Rack 1 (bottom left) */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '176px', height: '128px', left: '498.84px', top: '1373.9px' }}
          />

          {/* White Ads Desk */}
          <div
            className="absolute bg-[#FFFFFF] rounded-[6px]"
            style={{ width: '128px', height: '128px', left: '634.84px', top: '425.9px' }}
          />

          {/* Books Desk (tan) */}
          <div
            className="absolute bg-[#E8AD76] rounded-[6px]"
            style={{ width: '368px', height: '82px', left: '1198.84px', top: '425.9px' }}
          >
            <p
              className="absolute text-center"
              style={{
                width: '352px',
                height: '24px',
                left: '8px',
                top: '29px',
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: '-0.011em',
                color: 'rgba(0, 0, 0, 0.8)',
              }}
            >
              Books Desk
            </p>
          </div>

          {/* Orange Rack 7 (bottom) */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px]"
            style={{ width: '176px', height: '128px', left: '1042.84px', top: '1373.9px' }}
          />

          {/* Cyan Refrigerator Rack 3 */}
          <div
            className="absolute bg-[#5AD8CC] rounded-[6px]"
            style={{ width: '400px', height: '160px', left: '778.84px', top: '425.9px' }}
          />

          {/* Cyan Rack 5 */}
          <div
            className="absolute bg-[#5AD8CC] rounded-[6px]"
            style={{ width: '288px', height: '142px', left: '2146.84px', top: '409.9px' }}
          />

          {/* Cyan Rack 6 */}
          <div
            className="absolute bg-[#5AD8CC] rounded-[6px]"
            style={{ width: '288px', height: '142px', left: '2522.84px', top: '409.9px' }}
          />

          {/* Cyan Rack 7 */}
          <div
            className="absolute bg-[#5AD8CC] rounded-[6px]"
            style={{ width: '224px', height: '142px', left: '2970.84px', top: '409.9px' }}
          />

          {/* Purple Storage */}
          <div
            className="absolute bg-[#BB95F8] rounded-[6px]"
            style={{ width: '528px', height: '816px', left: '3370.84px', top: '409.9px' }}
          />

          {/* Gray Foot path */}
          <div
            className="absolute bg-[#D9D9D9] rounded-[6px]"
            style={{ width: '3408px', height: '176px', left: '490.84px', top: '1513.9px' }}
          />

          {/* Dark Gray Car Driveway */}
          <div
            className="absolute bg-[#757575] rounded-[6px]"
            style={{ width: '3408px', height: '225px', left: '482.84px', top: '1689.9px' }}
          />

          {/* Legend - Light blue rectangle */}
          <div
            className="absolute bg-[#C2E5FF] rounded-[6px]"
            style={{ width: '201.75px', height: '201.75px', left: '3001.84px', top: '1996.9px' }}
          />

          {/* Legend - Cyan rectangle */}
          <div
            className="absolute bg-[#5AD8CC] rounded-[6px] border-4 border-[#369E94]"
            style={{ width: '207px', height: '184px', left: '1331.84px', top: '1981.9px' }}
          />

          {/* Legend - Orange rectangle */}
          <div
            className="absolute bg-[#FF9E42] rounded-[6px] border-4 border-[#EB7500]"
            style={{ width: '207px', height: '184px', left: '482px', top: '1982px' }}
          />

          {/* Map Title */}
          <p
            className="absolute text-center"
            style={{
              width: '1960px',
              height: '144px',
              left: '1210.84px',
              top: '114px',
              fontFamily: 'Caveat, cursive',
              fontWeight: 700,
              fontSize: '96px',
              lineHeight: '150%',
              color: '#1E1E1E',
            }}
          >
            Map
          </p>

          {/* All text labels */}
          <p className="absolute left-[986.84px] text-[#1e1e1e] text-[24px] text-nowrap top-[968.17px] tracking-[-0.019em]">
            Fresh produce
          </p>
          <p className="absolute left-[1410.84px] text-[#1e1e1e] text-[24px] text-nowrap top-[952.17px] tracking-[-0.019em]">
            Fresh produce
          </p>
          <p className="absolute left-[498.84px] text-[#1e1e1e] text-[24px] text-center top-[1054.17px] tracking-[-0.019em] w-[176px]">
            Beverage
          </p>
          <p className="absolute left-[498.84px] text-[#1e1e1e] text-[24px] text-center top-[847.17px] tracking-[-0.019em] w-[176px]">
            Protien <br aria-hidden="true" />
            Shake
          </p>
          <p className="absolute left-[498.84px] text-[#1e1e1e] text-[24px] text-center top-[694.17px] tracking-[-0.019em] w-[176px]">
            Protien <br aria-hidden="true" />
            Shake
          </p>
          <p className="absolute left-[638.84px] text-[#1e1e1e] text-[24px] text-center top-[456.17px] tracking-[-0.019em] w-[120px]">
            Ads Desk
          </p>
          <p className="absolute left-[498.84px] text-[#1e1e1e] text-[24px] text-center top-[1199.17px] tracking-[-0.019em] w-[176px]">
            Ramen
          </p>
          <p className="absolute left-[498.84px] text-[#1e1e1e] text-[24px] text-center top-[1419.17px] tracking-[-0.019em] w-[176px]">
            Rack 1
          </p>
          <p className="absolute left-[1042.84px] text-[#1e1e1e] text-[24px] text-center top-[1420.17px] tracking-[-0.019em] w-[176px]">
            Rack 7
          </p>
          <p className="absolute left-[498.84px] text-[#1e1e1e] text-[24px] text-center top-[965.17px] tracking-[-0.019em] w-[176px]">
            Rack 2
          </p>
          <p className="absolute left-[890.84px] text-[#1e1e1e] text-[24px] text-center top-[480.17px] tracking-[-0.019em] w-[176px]">
            Refrigerator
          </p>
          <p className="absolute left-[890.84px] text-[#1e1e1e] text-[24px] text-center top-[516.17px] tracking-[-0.019em] w-[176px]">
            Rack 3
          </p>
          <p className="absolute left-[978.84px] text-[#1e1e1e] text-[24px] text-center top-[1004.17px] tracking-[-0.019em] w-[176px]">
            Rack 4
          </p>
          <p className="absolute left-[1402.84px] text-[#1e1e1e] text-[24px] text-center top-[1001.17px] tracking-[-0.019em] w-[176px]">
            Rack 5
          </p>
          <p className="absolute left-[1610.84px] text-[#1e1e1e] text-[24px] text-center top-[883.17px] tracking-[-0.019em] w-[176px]">
            Rack 1
          </p>
          <p className="absolute left-[2098.84px] text-[#1e1e1e] text-[24px] text-center top-[648.17px] tracking-[-0.019em] w-[176px]">
            Rack 2
          </p>
          <p className="absolute left-[2202.84px] text-[#1e1e1e] text-[24px] text-center top-[467.17px] tracking-[-0.019em] w-[176px]">
            Rack 5
          </p>
          <p className="absolute left-[2586.84px] text-[#1e1e1e] text-[24px] text-center top-[463.17px] tracking-[-0.019em] w-[176px]">
            Rack 6
          </p>
          <p className="absolute left-[2994.84px] text-[#1e1e1e] text-[24px] text-center top-[463.17px] tracking-[-0.019em] w-[176px]">
            Rack 7
          </p>
          <p className="absolute left-[2994.84px] text-[#1e1e1e] text-[24px] text-center top-[1096.17px] tracking-[-0.019em] w-[176px]">
            Empty Boxes
          </p>
          <p className="absolute left-[2994.84px] text-[#1e1e1e] text-[24px] text-center top-[780.17px] tracking-[-0.019em] w-[176px]">
            Rack 8
          </p>
          <p className="absolute left-[2578.84px] text-[#d9d9d9] text-[24px] text-center top-[712.17px] tracking-[-0.019em] w-[176px]">
            Packing Table 1
          </p>
          <p className="absolute left-[2578.84px] text-[#d9d9d9] text-[24px] text-center top-[988.17px] tracking-[-0.019em] w-[176px]">
            Packing Table 2
          </p>
          <p className="absolute left-[2098.84px] text-[#1e1e1e] text-[24px] text-center top-[873.17px] tracking-[-0.019em] w-[176px]">
            Rack 3
          </p>
          <p className="absolute left-[2098.84px] text-[#1e1e1e] text-[24px] text-center top-[1091.17px] tracking-[-0.019em] w-[176px]">
            Rack 4
          </p>
          <p className="absolute left-[3546.84px] text-[#1e1e1e] text-[24px] text-center top-[780.17px] tracking-[-0.019em] w-[176px]">
            Storage
          </p>
          <p className="absolute left-[2122.84px] text-[#1e1e1e] text-[40px] text-nowrap top-[1577.9px] tracking-[-0.022em]">
            Foot path
          </p>
          <p className="absolute left-[1989.84px] text-[64px] text-nowrap text-white top-[1754.62px] tracking-[-0.022em]">
            Car Driveway
          </p>
          <p className="absolute left-[2467.85px] text-[#1e1e1e] text-[40px] text-nowrap top-[2068.26px] tracking-[-0.022em]">
            User Interaction points
          </p>
          <p className="absolute left-[1640.84px] text-[#1e1e1e] text-[40px] text-nowrap top-[2052.35px] tracking-[-0.022em]">
            Refrigerated Racks
          </p>
          <p className="absolute left-[761px] text-[#1e1e1e] text-[40px] text-nowrap top-[2044px] tracking-[-0.022em]">
            Regular Racks
          </p>
          <p className="absolute left-[3277.93px] text-[#1e1e1e] text-[40px] text-nowrap top-[2065.74px] tracking-[-0.022em]">
            Volunteer Access area
          </p>
          <p className="absolute left-[3565px] text-[#1e1e1e] text-[40px] text-nowrap top-[249px] tracking-[-0.022em]">
            User Access Area
          </p>

          {/* Static Legend Star */}
          <div
            className="absolute"
            style={{
              left: '2175.84px',
              top: '1966.9px',
              width: '226.87px',
              height: '226.87px',
              transform: 'rotate(-8.01deg)',
            }}
          >
            <Star className="w-full h-full" />
          </div>
        </motion.div>

        {/* Animated Star Stickers */}
        {starPositions.map((star, index) => (
          <motion.div
            key={`${index}-${animationKey}`}
            initial={{ opacity: 0, scale: 0, rotate: star.rotate - 180 }}
            animate={
              isInView
                ? { opacity: 1, scale: 1, rotate: star.rotate }
                : { opacity: 0, scale: 0, rotate: star.rotate - 180 }
            }
            transition={{
              delay: animationKey === 0 ? 0.8 + index * 0.15 : 0,
              duration: 0.5,
              type: 'spring',
              stiffness: 200,
              damping: 15,
              repeat: animationKey === 1 ? Infinity : 0,
              repeatDelay: 2.5,
              repeatType: 'reverse',
            }}
            className="absolute"
            style={{
              left: `${star.left}px`,
              top: `${star.top}px`,
              width: `${star.size}px`,
              height: `${star.size}px`,
            }}
          >
            <Star className="w-full h-full" />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PantryMap;
